import { readCredentials, saveCredentials } from '../auth/store.js';
import { isAgentCredential } from '../storage/secrets.js';
import {
  AuthError,
  ApiError,
  ClientOutdatedError,
  NetworkError,
  PermissionError,
  ConflictError,
  RateLimitError,
  SessionWindowError,
  FeatureDisabledError,
  UnexpectedError,
  AppError,
  type CliError,
} from '../output/error.js';
import {
  getEffectiveApiUrl,
  getEffectiveWorkosClientId,
} from '../config/env-profiles.js';
import { buildVersionHeaders } from './version-headers.js';

// Module-level workspace context populated by the top-level CLI entry after
// parsing --workspace. Explicit options.workspaceId on a specific apiClient()
// call always wins; this is the fallback used by every other call site, so
// subcommands like `env` don't have to remember to thread the header through.
let workspaceCtx: { workspaceId: string | null } = { workspaceId: null };

export function setWorkspaceContext(ctx: { workspaceId: string | null }): void {
  workspaceCtx = ctx;
}

function decodeJwtExp(token: string): number {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(Buffer.from(payload, 'base64').toString());
    return decoded.exp ?? 0;
  } catch {
    return 0;
  }
}

async function refreshToken(
  refreshTokenValue: string,
  organizationId?: string,
): Promise<{ accessToken: string; refreshToken: string; expiresAt: number }> {
  const params = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshTokenValue,
    client_id: getEffectiveWorkosClientId(),
  });
  if (organizationId) {
    params.set('organization_id', organizationId);
  }
  const res = await fetch('https://api.workos.com/user_management/authenticate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: params,
  });

  if (!res.ok) {
    // Throw a typed AppError so the top-level exit-code mapper + Sentry
    // capture get a severity-tagged event instead of a bare Error. The caller
    // (forceTokenRefresh / apiClient) catches this and surfaces a friendly
    // AuthError('Session expired. Run: hookmyapp login') to the user.
    throw new UnexpectedError('refresh failed', 'WORKOS_REFRESH_FAILED');
  }

  const data = await res.json().catch(() => null);
  // Shape guard — a 200 with missing/empty tokens must NOT be persisted, or
  // saveCredentials would corrupt the store (JSON.stringify drops undefined
  // fields, leaving a credentials.json with no tokens at all).
  if (
    typeof data?.access_token !== 'string' || data.access_token === '' ||
    typeof data?.refresh_token !== 'string' || data.refresh_token === ''
  ) {
    throw new UnexpectedError('refresh response malformed', 'WORKOS_REFRESH_FAILED');
  }
  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: decodeJwtExp(data.access_token),
  };
}

export async function forceTokenRefresh(organizationId?: string): Promise<void> {
  const creds = await readCredentials();
  if (!creds) {
    throw new AuthError('Not logged in. Run: hookmyapp login');
  }
  // Agent (auth.md) credentials are org-scoped Bearer tokens with no refresh
  // token; there is nothing to refresh and no org re-scope to perform.
  if (isAgentCredential(creds)) {
    return;
  }
  try {
    const refreshed = await refreshToken(creds.refreshToken, organizationId);
    await saveCredentials(refreshed);
  } catch {
    throw new AuthError('Session expired. Run: hookmyapp login');
  }
}

// Centralized HTTP-status → AppError subclass mapping. Every non-ok response
// from apiClient funnels through here so that error shape/exit codes stay
// consistent across commands. Keep this in sync with the error-hierarchy
// contract in output/error.ts (exit codes: 2 / 3 / 4 / 5 / 6).
//
// Return type is `CliError` — a Phase 108 alias that is identical at runtime
// to `AppError` under Phase 123 Plan 10. Existing callers see no change.
export async function mapApiError(res: Response): Promise<CliError> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const body: any = await res.json().catch(() => ({ message: res.statusText }));
  const msg: string = body?.message ?? body?.error ?? res.statusText;
  const code: string | undefined = body?.code;

  if (res.status === 401) return new AuthError();
  if (res.status === 403) {
    // Sandbox-proxy 24h-window rejections surface verbatim — not a permission
    // problem, the developer needs the actionable "ask the recipient to
    // message first" guidance.
    if (code === 'SESSION_WINDOW_CLOSED') {
      return new SessionWindowError(msg);
    }
    // Feature-availability rejections (e.g. INSTAGRAM_DISABLED) surface the
    // server's message verbatim — "contact your workspace admin" would be
    // wrong guidance for a feature the server has turned off.
    if (code === 'INSTAGRAM_DISABLED') {
      return new FeatureDisabledError(msg, code);
    }
    // Lazy-import to avoid a cycle with commands/workspace.ts
    const { readWorkspaceConfig } = await import('../commands/workspace.js');
    const cfg = readWorkspaceConfig();
    return new PermissionError(cfg.activeWorkspaceSlug ?? '<unknown>');
  }
  if (res.status === 409) return new ConflictError(msg, code ?? 'CONFLICT');
  // Phase 122 — bootstrap-code exchange error mapping.
  // 404 and 410 collapse to the same user-facing message (oracle-attack
  // defense — brute-force guessers can't distinguish "unknown code" from
  // "already spent code"). ApiError.exitCode defaults to 1; we override
  // to 5 on the instance so the CLI's exit-code contract stays honest
  // ("API rejected the bootstrap code" is a distinct failure class).
  // AIT-51: a stale activeWorkspaceId (DB re-seed, deleted workspace) makes
  // every workspace-scoped command 404 — point at the recovery command.
  if (res.status === 404 && code === 'WORKSPACE_NOT_FOUND') {
    return new ApiError(`${msg}. Run: hookmyapp workspace use <name>`, 404);
  }
  if (res.status === 404 && code === 'BOOTSTRAP_NOT_FOUND') {
    const err = new ApiError(
      'Code invalid or already used. Ask the dashboard user to click Copy again.',
      404,
    );
    err.exitCode = 5;
    return err;
  }
  if (res.status === 410 && code === 'BOOTSTRAP_EXPIRED_OR_USED') {
    const err = new ApiError(
      'Code expired or already used. Ask the dashboard user to click Copy again.',
      410,
    );
    err.exitCode = 5;
    return err;
  }
  // The Stripe billing portal endpoints were retired — any call that still
  // reaches one gets a pointer to the in-app Billing page instead of a raw 410.
  if (res.status === 410 && code === 'BILLING_PORTAL_RETIRED') {
    return new ApiError(
      'This billing endpoint was retired. Manage billing from your Billing page in the app. Run: hookmyapp billing manage',
      410,
    );
  }
  if (res.status === 429) {
    // Phase 123 Plan 10 — use the new RateLimitError class (sev3, httpStatus
    // 429). Exit code remains 6 so the Phase 108 exit-code contract for 429
    // is preserved (historically this flowed through ConflictError → exit 6).
    // The body.code 'RATE_LIMITED' matches the backend's
    // UserIdThrottlerGuard structured 429 body.
    return new RateLimitError(
      msg && msg !== 'Too Many Requests'
        ? msg
        : 'Too many codes minted. Wait a minute and retry.',
      'RATE_LIMITED',
    );
  }
  if (res.status >= 500) {
    return new ApiError('Something went wrong on our end. Try again later.', res.status);
  }
  return new ApiError(msg, res.status);
}

export function isNetworkFailure(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const code = (err as any)?.code;
  if (code === 'ECONNREFUSED' || code === 'ENOTFOUND' || code === 'ECONNRESET' || code === 'ETIMEDOUT') {
    return true;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const message = (err as any)?.message;
  if (typeof message === 'string' && /fetch failed/i.test(message)) return true;
  return false;
}

/** Undici wraps the real failure ("getaddrinfo ENOTFOUND …", cert errors) in
 * `err.cause` behind a generic "fetch failed" — surface the inner message. */
export function describeFetchError(err: unknown): string {
  const cause = (err as { cause?: unknown })?.cause;
  const inner = cause instanceof Error ? cause.message : undefined;
  const outer = err instanceof Error ? err.message : String(err);
  return inner && inner !== outer ? `${outer} (${inner})` : outer;
}

export async function apiClient(
  path: string,
  options?: RequestInit & { workspaceId?: string },
): Promise<any> {
  const creds = await readCredentials();
  if (!creds) {
    throw new AuthError('Not logged in. Run: hookmyapp login');
  }

  // Phase 123 Plan 10 — set Sentry user tag on every authenticated API call.
  // Idempotent + lazy (no-op when telemetry off or Sentry not initialized).
  // Fire-and-forget so the API call's latency isn't gated on dynamic import.
  void (async () => {
    try {
      const { setCliUserFromCreds } = await import('../observability/sentry.js');
      await setCliUserFromCreds();
    } catch {
      // Swallow — telemetry must never affect API calls.
    }
  })();

  let { accessToken } = creds;

  // Check if token is expired (with 60-second buffer)
  const exp = decodeJwtExp(accessToken);
  if (exp > 0 && Date.now() / 1000 > exp - 60) {
    try {
      const refreshed = await refreshToken(creds.refreshToken);
      await saveCredentials(refreshed);
      accessToken = refreshed.accessToken;
    } catch {
      throw new AuthError('Session expired. Run: hookmyapp login');
    }
  }

  const baseUrl = getEffectiveApiUrl();

  const { workspaceId, ...fetchOptions } = options ?? {};

  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
    ...buildVersionHeaders(),
    ...(fetchOptions.headers as Record<string, string> ?? {}),
  };

  // Precedence: explicit options.workspaceId wins, else fall back to the
  // global --workspace context. /workspaces is the discovery endpoint — we
  // intentionally never inject (chicken-and-egg: the user is fetching the
  // list precisely to pick a workspace).
  const resolvedWsId = workspaceId !== undefined ? workspaceId : workspaceCtx.workspaceId;
  if (resolvedWsId && !path.startsWith('/workspaces')) {
    headers['X-Workspace-Id'] = resolvedWsId;
  }

  let res: Response;
  try {
    res = await fetch(`${baseUrl}${path}`, {
      ...fetchOptions,
      headers,
    });
  } catch (err) {
    if (isNetworkFailure(err)) {
      // Name the host + underlying cause (AIT-88) — the bare copy made
      // network failures undiagnosable in nightly logs.
      throw new NetworkError(
        `Could not connect to HookMyApp API (${new URL(baseUrl).host}): ${describeFetchError(err)}. Check your internet connection or try again later.`,
      );
    }
    throw err;
  }

  // Soft-warn — server signals "client below min_recommended" via the response
  // header. Banner is informational; the underlying response continues to flow.
  // Suppressed by NO_UPDATE_NOTIFIER=1 (npm/AWS CDK/Vue CLI/Yeoman convention).
  maybePrintOutdatedBanner(res);

  // Hard block — 426 Upgrade Required (RFC 9110 §15.5.16). Spec payload shape:
  // { code, outdated[], minVersions{}, messages[] }. Print the messages, throw
  // a ClientOutdatedError so the top-level mapper exits 1 cleanly.
  if (res.status === 426) {
    throw await parseClientOutdated(res);
  }

  if (!res.ok) {
    throw await mapApiError(res);
  }

  // 204 No Content (and other empty-body 2xx responses) have no JSON to parse.
  // Returning undefined avoids `Unexpected end of JSON input` in callers that
  // don't consume the return value (e.g. the heartbeat loop).
  if (res.status === 204) {
    return undefined;
  }
  try {
    return await res.json();
  } catch {
    // Empty or non-JSON 2xx body — treat as void rather than crashing with
    // "Unexpected end of JSON input" for callers that don't consume the return.
    return undefined;
  }
}

// --- Phase 126 sandbox bind-code contract ---
//
// Mirrors backend/src/sandbox/bind-code.controller.ts (Plan 03 Wave 2 locked
// contract: `GET /sandbox/bind-code` returns the caller's available bind code
// for the active workspace. The code doubles as the consumed-session bearer
// token once a phone claims it via an inbound WhatsApp message). The backend
// populates `consumedSessionId` only after the code has been consumed — the
// CLI polls until that field is present, then fetches the session detail via
// the existing `GET /sandbox/sessions/:sessionPublicId` path.
export interface BindCodeResponse {
  code: string;
  issuedAt: string; // ISO timestamp
  consumedSessionId?: string; // ssn_<8> publicId; present iff the code was consumed
}

/**
 * Fetch the caller's available bind code for the active workspace. Thin
 * wrapper around `apiClient` so the CLI commands get a typed return +
 * centralized retry/error-mapping through the main client.
 *
 * Errors funnel through `mapApiError`: 401 → AuthError (exit 4), 409 →
 * ConflictError (exit 6), 5xx → ApiError (exit 1) — same contract every other
 * typed helper in this file honors.
 */
export async function getBindCode(
  workspaceId: string,
): Promise<BindCodeResponse> {
  return (await apiClient('/sandbox/bind-code', {
    method: 'GET',
    workspaceId,
  })) as BindCodeResponse;
}

// --- Phase 2 of CLI + skill version enforcement (spec 2026-05-06) ---

/**
 * Print the soft-warn banner if the response carries `X-HookMyApp-Client-Outdated`.
 * Header value is a comma-separated list of components (e.g. "cli", "skill",
 * "cli,skill"). Banner is suppressed when NO_UPDATE_NOTIFIER=1.
 *
 * Banner is informational only — the original response continues to flow.
 * Printed once per process (a single CLI command makes O(1) backend calls,
 * but we still de-dupe defensively in case of paginated/poll loops).
 */
let bannerPrinted = false;
function maybePrintOutdatedBanner(res: Response): void {
  if (bannerPrinted) return;
  if (process.env.NO_UPDATE_NOTIFIER === '1') return;
  // Defensive: tests use minimal Response mocks that may omit `headers`. The
  // real fetch() always populates it, but `?.get` keeps the soft-warn path
  // crash-proof when the mock skips the field.
  const outdated = res.headers?.get('x-hookmyapp-client-outdated');
  if (!outdated) return;
  const parts = outdated.split(',').map((s) => s.trim()).filter(Boolean);
  const lines: string[] = [];
  if (parts.includes('cli')) {
    lines.push('A newer hookmyapp CLI is available. Update: npm install -g @gethookmyapp/cli@latest');
  }
  if (parts.includes('skill')) {
    lines.push('A newer integrate-hookmyapp skill is available. Update: npx skills add hookmyapp/agent-skills@latest');
  }
  if (lines.length === 0) return;
  process.stderr.write('\n' + lines.join('\n') + '\n\n');
  bannerPrinted = true;
}

interface ClientOutdatedPayload {
  code?: string;
  outdated?: string[];
  minVersions?: { cli?: string; skill?: string };
  messages?: string[];
}

/**
 * Parse a 426 body into a ClientOutdatedError. Defensive against malformed
 * payloads — falls back to a generic upgrade-required message so the CLI
 * never crashes on a server-side regression in payload shape.
 */
async function parseClientOutdated(res: Response): Promise<ClientOutdatedError> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const body: ClientOutdatedPayload = (await res.json().catch(() => ({}))) as any;
  const messages = Array.isArray(body.messages) && body.messages.length > 0
    ? body.messages.filter((m): m is string => typeof m === 'string')
    : ['HookMyApp CLI or skill needs an upgrade. Run: npm install -g @gethookmyapp/cli@latest'];
  return new ClientOutdatedError(messages, body.code ?? 'CLIENT_OUTDATED');
}
